import { useEffect, useCallback, useMemo } from 'react';
import { useQuery, QueryFunction, UseQueryOptions, UseQueryResult, QueryKey } from 'react-query';
import { isTruthy } from '@/utility';
import ExceptionManager from '@/managers/exception-manager';

export function useQueryProvider<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
	queryKey: Array<any>,
	queryFn: QueryFunction<TQueryFnData, QueryKey>,
	options: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'>,
	forceEnabled: boolean = false
): UseQueryResult<TData, TError> {
	const queryKeys = useMemo(() => [...queryKey], [queryKey]);

	const updatedOptions: UseQueryOptions<TQueryFnData, TError, TData> = {
		...options,
		enabled: forceEnabled ? true : options.enabled
	};

	const queryResult = useQuery<TQueryFnData, TError, TData>({
		queryKey: queryKeys,
		queryFn,
		...updatedOptions
	});

	const refetchIfNeeded = useCallback(() => {
		if ((updatedOptions?.refetchOnWindowFocus || updatedOptions?.refetchOnMount === 'always') && isTruthy(updatedOptions?.enabled)) {
			queryResult.refetch().catch(ExceptionManager.errorHandler);
		}
	}, [updatedOptions?.enabled, updatedOptions?.refetchOnWindowFocus, updatedOptions?.refetchOnMount, forceEnabled, queryResult.refetch]);

	useEffect(() => {
		refetchIfNeeded();

		window.addEventListener('focus', refetchIfNeeded);

		return () => {
			window.removeEventListener('focus', refetchIfNeeded);
		};
	}, [refetchIfNeeded]);

	return queryResult;
}
